/**
 * Bulk RMA Actions troubleshooting page: inspect a store's bulk requests, their chunk ledger,
 * and a filtered page of per-return outcomes.
 */
import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { SingleValue } from 'react-select';

import { BULK_REQUEST_STATUS } from '../../../../services/api/bulk-rma-action-types';
import { IStore } from '../../../../services/api/troubleshooting-service';
import CurrentPortalStore from '../../../../stores/current-portal-store';
import { IOption, SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import { Button } from '../../../../components/inputs/button/button';
import { BUTTON_VARIANTS } from '../../../../components/inputs/button-variants';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import FontBody2 from '../../../../components/fonts/font-body2/font-body2';
import LinearLoader from '../../../../components/loaders/linear-loader/linear-loader';
import Pagination from '../../../../components/table/components/pagination/pagination';

import TroubleshootingStore from '../../troubleshooting-page-store';
import bulkRmaActionRequestsPagedListStore from './stores/bulk-rma-action-requests-paged-list-store';
import bulkRmaActionRequestDetailStore from './stores/bulk-rma-action-request-detail-store';
import RequestsTable from './components/requests-table/requests-table';
import RequestDetailPanel from './components/request-detail-panel/request-detail-panel';
import RequestCancelControls from './components/request-cancel-controls/request-cancel-controls';
import { REQUEST_STATUS_LABELS } from './utils/bulk-rma-action-labels';

import './bulk-rma-actions-page.scss';

const REQUEST_STATUS_FILTER_OPTIONS: IOption[] = [
    { value: '', label: 'All statuses' },
    ...Object.values(BULK_REQUEST_STATUS).map((status) => ({
        value: status,
        label: REQUEST_STATUS_LABELS[status],
    })),
];

const BulkRmaActionsPage: React.FC = observer(() => {
    const [chosenStore, setChosenStore] = useState<IOption | null>(null);
    const [statusOption, setStatusOption] = useState<IOption>(REQUEST_STATUS_FILTER_OPTIONS[0]);
    useEffect(() => {
        TroubleshootingStore.fetchStores();
        CurrentPortalStore.load();
    }, []);

    const portalId = CurrentPortalStore.currentPortal.id;
    const portalName = CurrentPortalStore.currentPortal.name;
    const portalDisplayName = CurrentPortalStore.currentPortal.displayName;

    useEffect(() => {
        if (chosenStore?.value !== portalId && portalId && portalName) {
            setChosenStore({
                value: portalId,
                label: portalName + (portalDisplayName ? ` (${portalDisplayName})` : ''),
            });
        }
    }, [portalId, portalName, portalDisplayName, chosenStore?.value]);

    const storeOptions = useMemo((): IOption[] => {
        return TroubleshootingStore.stores?.map((store: IStore): IOption => ({
            value: store.id,
            label: store.name + (store.display_name ? ` (${store.display_name})` : ''),
        })) || [];
    }, [TroubleshootingStore.stores]);

    useEffect(() => {
        if (!chosenStore?.value) return;
        bulkRmaActionRequestsPagedListStore.setStoreId(chosenStore.value);
        bulkRmaActionRequestsPagedListStore.setStatusFilter(
            (statusOption.value as BULK_REQUEST_STATUS) || '',
        );
        void bulkRmaActionRequestsPagedListStore.loadPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenStore?.value, statusOption.value]);

    const onStoreChange = (val: SingleValue<IOption>) => {
        if (!val) return;
        setChosenStore(val);
        bulkRmaActionRequestDetailStore.close();
    };

    const {
        list: requests,
        page: requestsPage,
        perPage: requestsPerPage,
        total: requestsTotal,
        isLoading: isLoadingRequests,
        error: requestsError,
    } = bulkRmaActionRequestsPagedListStore;

    const selectedRequestId = bulkRmaActionRequestDetailStore.requestId;
    const isDetailView = selectedRequestId != null;

    return (
        <div className="bulk-rma-actions-page">
            {!isDetailView && (
                <div className="card">
                    <h4>Bulk RMA actions</h4>
                    <p className="bulk-rma-actions-page__intro">
                        Inspect bulk mark-as-done requests for a store: outcome counts, the chunk ledger,
                        and per-return results. Not gated by the merchant feature flag so support can
                        inspect requests started before the flag was turned off.
                    </p>
                    <div className="controls">
                        <p className="head5">Store</p>
                        <SelectDropdown
                            required
                            value={chosenStore || undefined}
                            placeholder="Select store"
                            options={storeOptions}
                            onChange={onStoreChange}
                        />
                    </div>
                    <div className="controls">
                        <p className="head5">Request status</p>
                        <SelectDropdown
                            value={statusOption}
                            options={REQUEST_STATUS_FILTER_OPTIONS}
                            placeholder="Filter by status"
                            onChange={(val: SingleValue<IOption>) => {
                                setStatusOption(val ?? REQUEST_STATUS_FILTER_OPTIONS[0]);
                                bulkRmaActionRequestDetailStore.close();
                            }}
                        />
                    </div>
                </div>
            )}

            {isDetailView && chosenStore?.value ? (
                <div className="card results-card">
                    <div className="bulk-rma-actions-page__detail-header">
                        <div className="bulk-rma-actions-page__detail-header-toolbar">
                            <Button
                                text="Back to requests"
                                variant={BUTTON_VARIANTS.Secondary}
                                onClick={() => bulkRmaActionRequestDetailStore.close()}
                            />
                            <RequestCancelControls />
                        </div>
                        <h4>Request #{selectedRequestId}</h4>
                    </div>

                    <RequestDetailPanel />
                </div>
            ) : (
                <div className="card results-card">
                    <div className="results-card__header">
                        <h4>Requests</h4>
                    </div>

                    {isLoadingRequests && <LinearLoader />}

                    {requestsError && !isLoadingRequests && (
                        <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER} variant={FONT_VARIANT.BOLD}>
                            {requestsError}
                        </FontBody2>
                    )}

                    {!isLoadingRequests && !requestsError && requests.length === 0 && (
                        <FontBody2 color={FONT_COLOR.PRIMARY2}>
                            {statusOption.value
                                ? 'No bulk requests match the current filters.'
                                : 'No bulk requests found for this store.'}
                        </FontBody2>
                    )}

                    {!isLoadingRequests && !requestsError && requests.length > 0 && chosenStore?.value && (
                        <>
                            <RequestsTable
                                storeId={chosenStore.value}
                                requests={requests}
                            />
                            <Pagination
                                page={requestsPage}
                                perPage={requestsPerPage}
                                total={requestsTotal}
                                totalResultsTitle="requests"
                                onSetPage={(page) => {
                                    void bulkRmaActionRequestsPagedListStore.loadPage(page);
                                }}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
});

export default BulkRmaActionsPage;
